import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import Cookies from 'universal-cookie';

import { ApiResponseError } from '@/errors/ApiResponseError';
import { NetworkError } from '@/errors/NetworkError';
import { IHTTPRequestService } from '@/interfaces/IHTTPRequestService';
import { IApiResponseError } from '@/interfaces/api/IApiResponseError';
import { IRefreshSessionResponse } from '@/interfaces/auth/IRefreshSessionResponse';

const accessToken = new Cookies('accessToken', { path: '/' });
const refreshToken = new Cookies('refreshToken', { path: '/' });
const username = new Cookies('username', { path: '/' });

function createErrorHandler(instance: AxiosInstance) {
	return async function handleAxiosError(
		error: unknown | Error | AxiosError<IApiResponseError>,
	) {
		if (axios.isAxiosError(error) && !error.response) {
			if (
				error.code === 'ERR_NETWORK' ||
				error.message.includes('Network Error')
			) {
				return Promise.reject(
					new NetworkError(
						'Unable to connect to the internet. Please check your connection and try again.',
					),
				);
			}
			if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
				return Promise.reject(
					new NetworkError(
						'The request took too long to complete. Please check your internet connection and try again.',
					),
				);
			}
		}

		if (!axios.isAxiosError<IApiResponseError>(error) || !error.response)
			return Promise.reject(error);

		const originalRequest = error.config;
		const shouldRefresh = error.response.status === 401;
		if (
			shouldRefresh &&
			originalRequest &&
			originalRequest.url !== '/auth/refresh' &&
			originalRequest.url !== '/auth/sign-in' &&
			originalRequest.url !== '/auth/confirm-user' &&
			originalRequest.url !== '/auth/confirm-password'
		) {
			const refreshResponse = await instance.post<IRefreshSessionResponse>(
				'/auth/refresh',
				{
					username: username.get('username'),
					refreshToken: refreshToken.get('refreshToken'),
				},
			);
			accessToken.set('accessToken', refreshResponse.data.accessToken);
			const authorization = `Bearer ${refreshResponse.data.accessToken}`;
			instance.defaults.headers.common['Authorization'] = authorization;
			originalRequest.headers.Authorization = authorization;
			return instance(originalRequest);
		} else if (
			shouldRefresh &&
			originalRequest &&
			originalRequest.url === '/auth/refresh'
		) {
			accessToken.remove('accessToken');
			refreshToken.remove('refreshToken');
			username.remove('username');
			//redirect to login
			window.location.href = '/auth/sign-in';

			return Promise.reject(
				new ApiResponseError(
					error.response.data.error,
					error.response.data.message,
					error.response.data.statusCode,
				),
			);
		}
		return Promise.reject(
			new ApiResponseError(
				error.response.data.error,
				error.response.data.message,
				error.response.data.statusCode,
			),
		);
	};
}

export const axiosInstance = axios.create({
	baseURL: import.meta.env.VITE_API_URL,
	headers: {
		common: { Authorization: `Bearer ${accessToken.get('accessToken')}` },
	},
});

axiosInstance.interceptors.response.use(
	(response) => response,
	createErrorHandler(axiosInstance),
);

function createAxiosService(
	instance: AxiosInstance,
): IHTTPRequestService<AxiosRequestConfig> {
	const handleNetworkError = (error: unknown): never => {
		if (axios.isAxiosError(error) && !error.response) {
			if (
				error.code === 'ERR_NETWORK' ||
				error.message.includes('Network Error')
			) {
				throw new NetworkError(
					'Unable to connect to the internet. Please check your connection and try again.',
				);
			}
			if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
				throw new NetworkError(
					'The request took too long to complete. Please check your internet connection and try again.',
				);
			}
		}
		throw error;
	};

	return {
		get: async <T,>(url: string, config?: AxiosRequestConfig): Promise<T> => {
			try {
				const response = await instance.get<T>(url, config);
				return response.data;
			} catch (error) {
				throw handleNetworkError(error);
			}
		},
		post: async <T, K = unknown>(
			url: string,
			body: K,
			config?: AxiosRequestConfig,
		): Promise<T> => {
			try {
				const response = await instance.post<T>(url, body, config);
				return response.data;
			} catch (error) {
				throw handleNetworkError(error);
			}
		},
		patch: async <T, K = unknown>(
			url: string,
			body: K,
			config?: AxiosRequestConfig,
		): Promise<T> => {
			try {
				const response = await axiosInstance.patch(url, body, config);
				return response.data;
			} catch (error) {
				throw handleNetworkError(error);
			}
		},
		put: async <T, K = unknown>(
			url: string,
			body: K,
			config?: AxiosRequestConfig,
		): Promise<T> => {
			try {
				const response = await axiosInstance.put(url, body, config);
				return response.data;
			} catch (error) {
				throw handleNetworkError(error);
			}
		},
		delete: async <T,>(
			url: string,
			config?: AxiosRequestConfig,
		): Promise<T> => {
			try {
				const response = await axiosInstance.delete<T>(url, config);
				return response.data;
			} catch (error) {
				throw handleNetworkError(error);
			}
		},
		setAuthentication: (token: string) => {
			instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
			return;
		},
	};
}

export const axiosService = createAxiosService(axiosInstance);
